import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import Config from 'config';
import KEY_ORGANIZATION_CLASSROOM_ENGAGEMENT_ASYNC_QUERY from 'graph/reports/key-organization-classroom-engagement-report-async-query';
import { Typography } from '@material-ui/core';
import { sortAlphabeticallyByKeys } from 'utilities/sort-util';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import { ReportView, ReportEntityType } from '../../../../types';
import { resolveDateRange } from '../../../../utils/report-date-utils';
import EngagementChartView from './EngagementChartView';
import EngagementTableView from './EngagementTableView';

const CLASSROOM_NAME_SORT = (a: any, b: any): number =>
  sortAlphabeticallyByKeys(['classroom.name'], a, b);

const ClassroomEngagementCards: React.FC = () => {
  const [{ entityId, entityType, dateRange, viewAs }] = useReportQueryParams();
  const { t } = useTranslation('Reports');
  const [fetchReport, { data, error, stopPolling }] = useLazyQuery(
    KEY_ORGANIZATION_CLASSROOM_ENGAGEMENT_ASYNC_QUERY,
    {
      pollInterval: Config.reporting.asyncPollIntervalMs
    }
  );
  const dateRangeObject = useMemo(() => resolveDateRange(dateRange), [dateRange]);

  useEffect(() => {
    const entityTypeParam = entityType === ReportEntityType.USER ? 'userId' : 'organizationId';
    fetchReport({
      variables: { where: { [entityTypeParam]: entityId as number }, dateRange: dateRangeObject }
    });
  }, [entityType, entityId, fetchReport, dateRangeObject]);

  let reportData: any = [];

  if (error) {
    stopPolling?.();
  } else if (data?.keyOrganizationClassroomEngagementReportAsync?.result) {
    stopPolling?.();
    reportData = data.keyOrganizationClassroomEngagementReportAsync.result
      .slice()
      .sort(CLASSROOM_NAME_SORT);
  }

  const REPORT_VIEW_MAP: { [key in ReportView]?: any } = {
    [ReportView.CHART]: (
      <EngagementChartView
        reportData={reportData}
        loading={!data?.keyOrganizationClassroomEngagementReportAsync?.result}
      />
    ),
    [ReportView.TABLE]: (
      <EngagementTableView
        reportData={reportData}
        loading={!data?.keyOrganizationClassroomEngagementReportAsync?.result}
      />
    )
  };

  return (
    <section>
      <Typography variant="h3">{t('Engagement')}</Typography>
      {REPORT_VIEW_MAP[viewAs]}
    </section>
  );
};

export default ClassroomEngagementCards;
